import { useNavigation } from "@-/com.npmjs.expo-router";
import { useEffect } from "react";
import useLatest from "@-/com.npmjs.use-latest";

type NavigationLike = {
  addListener: (event: "blur" | "focus", callback: () => void) => () => void;
  getParent: () => NavigationLike | undefined;
  isFocused: () => boolean;
};

function isNavigationTreeFocused(navigation: NavigationLike): boolean {
  for (
    let current: NavigationLike | undefined = navigation;
    current != null;
    current = current.getParent()
  ) {
    if (!current.isFocused()) {
      return false;
    }
  }
  return true;
}

/**
 * Run `refetch` every time this screen is the focused leaf of the whole navigator tree — i.e. on
 * mount and every time its bottom tab regains focus.
 *
 * Why not `useFocusEffect`: it subscribes to focus on the NEAREST navigator only. A bottom-tab
 * screen nested inside its own Stack/Slot (e.g. the Decks `active` screen, or Stats `characters`)
 * keeps its own `isFocused()` true when you switch to another bottom tab — only the parent tab
 * navigator blurs — so `useFocusEffect` fires on first mount but never again on return. This hook
 * instead subscribes to `focus` on the screen AND every ancestor navigator, and calls `refetch`
 * whenever the full ancestor chain is focused. Under `freezeOnBlur` the screen stays mounted, so
 * this listener-based approach is what actually re-runs the query on a return visit.
 *
 * The tree fires a `focus` event at every navigator level as focus cascades to the leaf, so one
 * return invokes the handler several times in the same tick. Coalesce that burst and explicitly
 * reuse an in-flight query: cached QueryObserver.refetch() calls otherwise CANCEL and restart
 * the ongoing refresh by default. (We deliberately DON'T try to suppress the first mount
 * refetch by tracking blur/focus edges — that ordering is delivered inconsistently across the
 * different tab nesting depths and would silently drop the refetch on some returns. One extra
 * background fetch on the first visit is the acceptable cost of a reliable return refetch.)
 *
 * `refetch` is read through a latest-ref so a fresh function identity each render doesn't tear
 * down and rebuild the listeners.
 */
export function useRefetchOnTabFocus(
  refetch: (options: { cancelRefetch: false }) => unknown,
): void {
  const navigation = useNavigation();
  const refetchRef = useLatest(refetch);

  useEffect(() => {
    const nav = navigation as NavigationLike;
    let disposed = false;
    let queued = false;
    const onFocus = () => {
      if (queued || !isNavigationTreeFocused(nav)) {
        return;
      }
      queued = true;
      queueMicrotask(() => {
        queued = false;
        // Navigation may blur or unmount this retained body before the focus cascade ends.
        if (!disposed && isNavigationTreeFocused(nav)) {
          void refetchRef.current({ cancelRefetch: false });
        }
      });
    };

    const unsubscribers: (() => void)[] = [];
    for (
      let current: NavigationLike | undefined = nav;
      current != null;
      current = current.getParent()
    ) {
      unsubscribers.push(current.addListener("focus", onFocus));
    }

    // Fire once on mount if already focused: a screen reached by a direct deep-link (not a tab
    // switch) may not receive a `focus` event, so the listener alone wouldn't refetch it.
    onFocus();

    return () => {
      disposed = true;
      for (const unsubscribe of unsubscribers) {
        unsubscribe();
      }
    };
  }, [navigation, refetchRef]);
}
